import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNotify } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Mail } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Drafts to Approve (docs/design/me-mockup.html). The engine WROTE these; nothing
// sends until a human approves (draft-for-approval, D17). Approve -> the
// send-draft edge function transmits (SMS/email) and logs it.

type Draft = {
  id: string; lead_id: string; channel: string; to_address: string | null;
  subject: string | null; body: string; created_by: string | null;
  lead?: { contact_name: string | null; address_line1: string | null; city: string | null };
};

async function sendDraft(draftId: string) {
  const { data: sess } = await getSupabaseClient().auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-draft`, {
    method: "POST",
    headers: {
      apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY as string,
      Authorization: `Bearer ${sess.session?.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ draft_id: draftId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `send failed (${res.status})`);
  return data;
}

const DraftRow = ({ d, onDone }: { d: Draft; onDone: () => void }) => {
  const notify = useNotify();
  const [body, setBody] = useState(d.body);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    await getSupabaseClient().from("drafts").update({ body }).eq("id", d.id);
    setEditing(false); notify("Draft updated", { type: "success" });
  };
  const approve = async () => {
    setBusy(true);
    try {
      if (editing) await save();
      await sendDraft(d.id);
      notify("Sent & logged", { type: "success" });
      onDone();
    } catch (e: any) { notify(`Send failed: ${e.message}`, { type: "error" }); setBusy(false); }
  };
  const discard = async () => {
    await getSupabaseClient().from("drafts").update({ status: "discarded" }).eq("id", d.id);
    notify("Draft discarded", { type: "info" }); onDone();
  };

  return (
    <Card className="mb-2">
      <CardContent className="p-3 flex gap-3">
        <div className={`w-9 h-9 rounded flex items-center justify-center shrink-0 ${d.channel === "sms" ? "bg-sky-100 text-sky-700" : "bg-purple-100 text-purple-700"}`}>
          {d.channel === "sms" ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm flex items-center gap-2">
            <Link to={`/leads/${d.lead_id}/show`} className="hover:underline">{d.lead?.contact_name || "(lead)"}</Link>
            <Badge variant="outline" className="uppercase text-[0.6rem]">{d.channel}</Badge>
            {d.created_by && <span className="text-xs text-muted-foreground">· {d.created_by}</span>}
          </div>
          <div className="text-xs text-muted-foreground truncate">{[d.lead?.address_line1, d.lead?.city].filter(Boolean).join(", ")} · to {d.to_address || "—"}</div>
          {editing
            ? <Textarea className="mt-2 text-sm" value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
            : <div className="mt-1 text-sm bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">{body}</div>}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="sm" disabled={busy || !d.to_address} onClick={approve}>{busy ? "Sending…" : "Approve & send"}</Button>
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>{editing ? "Cancel edit" : "Edit"}</Button>
          <Button size="sm" variant="ghost" onClick={discard}>Discard</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const DraftsPage = () => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    getSupabaseClient()
      .from("drafts")
      .select("id,lead_id,channel,to_address,subject,body,created_by,lead:leads_summary!lead_id(contact_name,address_line1,city)")
      .eq("status", "pending").order("created_at", { ascending: true })
      .then(({ data }) => {
        setDrafts((data as any[])?.map((d) => ({ ...d, lead: Array.isArray(d.lead) ? d.lead[0] : d.lead })) ?? []);
        setLoading(false);
      });
  };
  useEffect(load, []);

  return (
    <div className="p-4 max-w-3xl">
      <h1 className="text-xl font-semibold">Drafts to Approve</h1>
      <div className="text-sm text-muted-foreground mb-4">The engine wrote these — nothing sends until you approve (draft-for-approval).</div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : drafts.length === 0 ? <p className="text-sm text-muted-foreground">All caught up — no drafts waiting. 🎉</p>
        : drafts.map((d) => <DraftRow key={d.id} d={d} onDone={load} />)}
    </div>
  );
};

DraftsPage.path = "/drafts";
export default DraftsPage;
